import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { AudioTrackLabel } from '@features/advancedVideoPlayer';
import { DIRECT_CLIP_CUSTOM_FIELD_KEYS } from '../constants';
import { AudioLayoutConfig, Clip, DEFAULT_CLIP } from '../types';
import {
    BackendDirectClip,
    BackendMediaCustomFieldNames,
} from '../types/direct';
import {
    BackendMediaObject,
    BackendMediaObjectInstance,
    MediaIdentifier,
} from '../types/media';
import {
    mapDirectMediaIdentifiers,
    mapFrameRate,
    mapDirectMediaIdentifier,
} from './media.mappers';

const LANGUAGE_MAP: Record<string, string> = {
    xx: 'International',
    fr: 'French',
    en: 'English',
    de: 'German',
    es: 'Spanish',
    it: 'Italian',
};

const formatLanguageCode = (code?: string): string => {
    if (!code) return '-';
    const normalized = code.toLowerCase().trim();
    return LANGUAGE_MAP[normalized] ?? code.toUpperCase();
};

const mapCustomFields = <TTarget, TNames extends string>(
    customFields: Record<TNames, { value?: unknown }>,
    map: Partial<Record<keyof TTarget, TNames>>,
): Partial<TTarget> => {
    return Object.entries(map).reduce(
        (acc, [targetKey, sourceKey]) => ({
            ...acc,
            [targetKey]: customFields[sourceKey as TNames]?.value ?? '',
        }),
        {} as Partial<TTarget>,
    );
};

export const mapDirectClipToFrontend = (raw: BackendDirectClip): Clip => {
    const metadata = raw.clipMetadata ?? {};
    const mediaObject = raw.mediaObject ?? ({} as BackendMediaObject);
    const instances = mediaObject.mediaObjectInstances ?? [];

    const mappedCustomFields = mapCustomFields<
        Clip,
        BackendMediaCustomFieldNames
    >(raw.customFields, DIRECT_CLIP_CUSTOM_FIELD_KEYS);

    const soundfieldGroups =
        metadata.audioLayout?.groupOfSoundfieldGroups ?? [];

    const audioTracks: AudioTrackLabel[] = soundfieldGroups.flatMap(
        (parentGroup) =>
            (parentGroup.soundFieldGroups ?? []).flatMap((group) =>
                (group.audioChannels ?? []).map((ch) => ({
                    label: ch.TagSymbol,
                    tagName: ch.TagName,
                    groupName: parentGroup.TagName ?? '',
                    flowNativeAudioChannel: ch.flowNativeAudioChannel,
                })),
            ),
    );

    const audioLayouts: AudioLayoutConfig[] = soundfieldGroups.flatMap(
        (parentGroup) =>
            (parentGroup.soundFieldGroups ?? []).map((group) => {
                const channelCount = group.audioChannels?.length ?? 0;
                return {
                    mapping: `${channelCount} Track${channelCount > 1 ? 's' : ''}`,
                    label: '',
                    mix: group.TagName || 'Unknown Mix',
                    language: formatLanguageCode(group.lang),
                };
            }),
    );

    const hasProxy = instances.some(
        (inst) =>
            inst.type === 'PROXY' &&
            (!inst.materialInfo ||
                inst.materialInfo.onFlow === true ||
                inst.materialInfo.online === true),
    );

    const mediaIdentifiers = mapDirectMediaIdentifiers(instances);

    return mergeWithDefault(
        {
            ...mappedCustomFields,
            id: raw.id,
            mediaObjectId: raw.mediaObjectId,
            clipName:
                mediaObject.realName ||
                mediaObject.name ||
                metadata.cardName ||
                '',
            audioMapping: metadata.audioLayout?.templateLabel ?? '',
            created: raw.created ? new Date(raw.created).toISOString() : '',
            mediaIdentifiers,
            hasProxy,
            audioTracks,
            audioLayouts,
            frameRate: mapFrameRate(mediaObject.sampleRate),
            durationSeconds: mediaObject.durationSeconds,
            duration: mediaObject.duration,
        },
        DEFAULT_CLIP,
    );
};

export const mapDirectClipMediaToFrontend = (
    raw: BackendMediaObjectInstance,
): MediaIdentifier => {
    return mapDirectMediaIdentifier(raw);
};
